import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ativarDisciplina,
  lerDisciplinasDoAdmin,
  lerDocentes,
  lerFiltros,
  salvarDisciplina,
  type DisciplinaDoAdmin,
  type DocenteDoAdmin,
  type FiltrosDoAdminPos,
} from "../lib/adminPos";
import { CAMPO, CampoDeBusca, Recado } from "./ui";
import { useRolarAoEscolher } from "../lib/rolarPara";
import Carregando from "./Carregando";

// Admin › Faculdade › Disciplinas — o cadastro que o painel não tinha
// (000244-000247). Pedido de Victor em 16/09, junto com o de docentes.
//
// ---------------------------------------------------------------------------
// O que esta tela faz que a de Turmas não fazia
// ---------------------------------------------------------------------------
// Criar disciplina já existia (000228), mas só DENTRO de uma turma, e só criar:
// não havia como corrigir o nome, a carga horária ou o professor de uma que já
// existe — e são 245 delas em 9 cursos. Corrigir exigia SQL.
//
// Aqui a disciplina é a entidade, e a turma é o outro assunto: quem monta a
// grade continua sendo o editor de Turmas. Juntar as duas coisas faria "corrigir
// o nome de uma disciplina" exigir escolher uma turma antes.
//
// ---------------------------------------------------------------------------
// Os três números de cada linha
// ---------------------------------------------------------------------------
// Questões ativas, tentativas e turmas. Eles não são enfeite — são o que
// responde "mexer nisto alcança alguém?":
//
//   · **questões ativas** abaixo de 10 quer dizer que a prova NÃO funciona. São
//     22 disciplinas assim hoje, e antes desta tela não havia como ver isso sem
//     abrir cada uma.
//   · **tentativas** é o que torna apagar impossível: 270 mil no total.
//   · **turmas** é quantas grades a contêm, e é o número que explica por que
//     desativar não a tira da tela do aluno.
//
// ---------------------------------------------------------------------------
// A lista NÃO carrega sozinha: o curso vem primeiro
// ---------------------------------------------------------------------------
// Pedido de Victor em 16/09, depois de ver a tela: "tira o carregamento de todas
// as disciplinas ao abrir. O carregamento deve ser condicionado pela escolha do
// curso."
//
// A primeira versão abria buscando as 245 de todos os cursos. Duas coisas
// estavam erradas nisso, e nenhuma some com a paginação:
//
//   · **a ida de rede que ninguém pediu.** Quem abre esta aba vem consertar UMA
//     disciplina de UM curso; carregar os 9 antes de saber qual é gastar a
//     espera do operador com dado que ele descarta no clique seguinte.
//   · **a lista misturada não é um lugar de trabalho.** Ordenada por curso e
//     ordem, os 9 viram um paredão em que a disciplina procurada está sempre na
//     página 4 — e a paginação, que resolveu a ALTURA, não resolve isso: ela só
//     empurra o problema para o "mostrar mais".
//
// Então o estado inicial é o SELETOR sozinho, com o convite. É a mesma forma do
// Radar (um cliente por vez) e do editor de grade (uma turma por vez): a tela de
// trabalho começa pela escolha do recorte, não pelo acervo inteiro.
//
// ---------------------------------------------------------------------------
// Desativar não é o mesmo que tirar da grade, e a tela DIZ isso
// ---------------------------------------------------------------------------
// Desativar tira das listas daqui e impede entrar em grade NOVA. A turma que já
// a cursa continua com ela — mexer nisso reescreveria a grade de turmas em
// andamento, que é outra decisão e tem outra tela. A resposta do banco traz a
// frase com o número de turmas, e ela aparece inteira aqui.

type Formulario = {
  id: string | null;
  curso: string;
  nome: string;
  cargaHoraria: string;
  docenteId: string;
};

export default function AdminPosDisciplinas() {
  const [lista, setLista] = useState<DisciplinaDoAdmin[] | null>(null);
  const [docentes, setDocentes] = useState<DocenteDoAdmin[]>([]);
  const [filtros, setFiltros] = useState<FiltrosDoAdminPos | null>(null);
  const [curso, setCurso] = useState("");
  const [busca, setBusca] = useState("");
  const [mostrarInativas, setMostrarInativas] = useState(false);
  const [semDocente, setSemDocente] = useState(0);
  const [inativas, setInativas] = useState(0);
  const [form, setForm] = useState<Formulario | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);
  // Quantas aparecem. Medido com o instrumento: as 245 de uma vez dão **52.728px**
  // — 58,6 telas —, que é a cicatriz da fila do admin (41.910px, resolvida com o
  // mesmo "mostrar mais") e da busca de CNAE (8.521px). O passo é 25, como lá.
  const [mostrando, setMostrando] = useState(25);

  /**
   * O formulário mora ACIMA da lista, e quem clica em "Editar" numa linha do
   * fim não vê nada acontecer.
   *
   * Victor, em 16/09: *"quando estou em uma disciplina no final da lista e
   * clico em editar, ele não scrolla até a caixa de edição, parece que o
   * sistema não responde, mas na verdade funciona, só não scrolla"*. É a
   * terceira vez que esta casa encontra o mesmo modo de falha — o Mapa de
   * Concursos, o Calendário e o Admin › Alunos, todos em 28/08 —, e é o pior
   * tipo: a tela RESPONDE, fora do campo de visão, então a pessoa conclui que
   * o clique não pegou.
   *
   * `useRolarAoEscolher` é a peça que nasceu daquele dia: ela desconta o Topo fixo pela
   * variável `--cf-topo` (nada de altura cravada), respeita
   * `prefers-reduced-motion` e NÃO rola quando o alvo já
   * está inteiro na tela — abrir o formulário com a lista no topo não deve
   * mover nada.
   *
   * O gatilho é o id do que está sendo editado, e "novo" para a criação: os
   * dois mudam a cada abertura, inclusive quando se troca de uma linha para
   * outra sem fechar o formulário.
   */
  const caixaDoForm = useRef<HTMLElement | null>(null);
  // UMA expressão para o estado "qual formulário está aberto", usada pela
  // rolagem e pelo foco: duas seriam duas definições da mesma coisa, e a que
  // divergisse seria a que ninguém lê.
  const formAberto = form ? (form.id ?? "novo") : null;
  useRolarAoEscolher(caixaDoForm, formAberto);

  // A peça acima rola e NÃO move o foco, de propósito: ela nasceu para
  // FILTROS, onde puxar o foco quebraria a sequência de quem escolhe pelo
  // teclado. Abrir um formulário é outro gesto — quem clicou em "Editar"
  // quer estar DENTRO dele, que é o que um <dialog> faria.
  //
  // `preventScroll` é obrigatório aqui: sem ele o navegador rola por conta
  // própria para revelar o campo, e ele não sabe do Topo fixo — o campo
  // pararia escondido atrás dele, desfazendo o que a peça acabou de fazer.
  useEffect(() => {
    if (formAberto === null) return;
    const t = window.setTimeout(() => {
      const campo = caixaDoForm.current?.querySelector<HTMLInputElement>('[data-campo="nome"]');
      campo?.focus({ preventScroll: true });
    }, 80);
    return () => window.clearTimeout(t);
  }, [formAberto]);

  // Ao abrir, SÓ a lista de cursos — que é o que o seletor precisa para existir.
  // As disciplinas e os docentes esperam a escolha.
  useEffect(() => {
    void (async () => setFiltros(await lerFiltros()))();
  }, []);

  const carregar = useCallback(async () => {
    // Sem curso não há consulta. `lista` volta a null, que é o estado "ainda
    // não escolheram" — diferente de `[]`, que é "escolheram e não há
    // nenhuma". Os dois têm telas diferentes, e tratá-los como o mesmo faria o
    // convite virar "nenhuma disciplina neste recorte", que é falso.
    if (!curso) {
      setLista(null);
      setInativas(0);
      setSemDocente(0);
      return;
    }
    const r = await lerDisciplinasDoAdmin({
      curso,
      busca: busca.trim() || null,
      incluirInativas: mostrarInativas,
    });
    if (!r) {
      setLista([]);
      setRecado({ tipo: "erro", texto: "Não conseguimos carregar as disciplinas." });
      return;
    }
    setLista(r.disciplinas ?? []);
    setInativas(r.inativas ?? 0);
    setSemDocente(r.sem_docente ?? 0);
  }, [curso, busca, mostrarInativas]);

  useEffect(() => {
    const t = setTimeout(() => void carregar(), 250);
    return () => clearTimeout(t);
  }, [carregar]);

  // Os docentes ATIVOS, para o seletor do formulário. Desativado não entra em
  // disciplina nova — quem recusa é o banco, e a tela não o oferece para o
  // operador não descobrir isso só ao salvar.
  //
  // Eles vêm junto da PRIMEIRA escolha de curso, e não ao abrir a aba nem ao
  // abrir o formulário: no primeiro caso seriam a ida de rede que este pedido
  // veio tirar; no segundo, o seletor de professor nasceria vazio e se
  // preencheria sozinho na frente de quem já está escolhendo.
  useEffect(() => {
    if (!curso || docentes.length) return;
    void (async () => setDocentes((await lerDocentes())?.docentes ?? []))();
  }, [curso, docentes.length]);

  // Trocar de recorte volta ao topo da lista: continuar em "mostrando 150" depois
  // de filtrar por outro curso mostraria uma lista inteira que ninguém pediu.
  useEffect(() => {
    setMostrando(25);
  }, [curso, busca, mostrarInativas]);

  // O agregado vai para o TOPO, e o detalhe só onde é problema. Com o número em
  // cada linha, "10 de 10 questões" aparecia **213 vezes** na mesma tela — e a
  // regra da casa é contar quantas vezes a mesma informação se repete antes de
  // dar a tela por pronta.
  const incompletas = useMemo(
    () => (lista ?? []).filter((d) => d.questoes_ativas < 10).length,
    [lista],
  );

  const cursos = useMemo(() => filtros?.cursos ?? [], [filtros]);

  async function salvar() {
    if (!form) return;
    setSalvando(true);
    setRecado(null);
    const carga = form.cargaHoraria.trim();
    const r = await salvarDisciplina({
      id: form.id,
      curso: form.curso,
      nome: form.nome,
      // Campo vazio é NULO, e não zero: zero afirmaria uma carga horária de zero
      // hora, que o banco recusa — e recusaria com razão. "Não informado" e
      // "informado como zero" são coisas diferentes.
      cargaHoraria: carga === "" ? null : Number(carga),
      docenteId: form.docenteId || null,
    });
    setSalvando(false);

    if (!r.ok) {
      setRecado({ tipo: "erro", texto: r.detalhe ?? "Não conseguimos salvar." });
      return;
    }
    // O `aviso` é o terceiro estado do princípio 2: a disciplina foi criada E a
    // prova dela ainda não funciona. Ele vem do banco e aparece inteiro.
    setRecado({
      tipo: "ok",
      texto: r.aviso ? r.aviso.replace(/^aviso:\s*/, "") : (r.detalhe ?? "Salvo."),
    });
    setForm(null);
    await carregar();
  }

  async function alternarAtiva(d: DisciplinaDoAdmin) {
    setRecado(null);
    const r = await ativarDisciplina(d.id, !d.ativa);
    if (!r.ok) {
      setRecado({ tipo: "erro", texto: r.detalhe ?? "Não conseguimos mudar o estado." });
      return;
    }
    setRecado({ tipo: "ok", texto: r.detalhe ?? "" });
    await carregar();
  }

  // `filtros === null` é o carregamento da lista de cursos — o único que
  // acontece ao abrir. `lista === null` NÃO é carregamento: é "ainda não
  // escolheram", e tem tela própria mais abaixo.
  if (filtros === null) return <Carregando />;

  return (
    <div className="flex flex-col gap-5">
      {/* ── A barra ────────────────────────────────────────────────────────── */}
      <div className="surface rise p-5 sm:p-6">
        <div className="flex flex-wrap items-end gap-4">
          <label className="flex min-w-56 flex-col gap-1.5">
            <span className="text-sm font-semibold text-tinta">Curso</span>
            <select
              data-filtro="curso"
              className={CAMPO}
              value={curso}
              onChange={(e) => setCurso(e.target.value)}
            >
              {/* Não há "todos os cursos", de propósito: ele seria o caminho
                  de volta para o carregamento que este pedido veio tirar. */}
              <option value="">Escolha um curso…</option>
              {cursos.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.nome ?? c.slug}
                </option>
              ))}
            </select>
          </label>

          {/* Busca, inativas e "nova" só existem DEPOIS da escolha: uma busca
              sem curso não teria onde procurar, e "+ Nova disciplina" teria de
              adivinhar o curso — a primeira versão pegava `cursos[0]`, que é a
              Perícia por ordem alfabética e não tem relação nenhuma com o que o
              operador veio fazer. */}
          {curso && (
            <>
              <div className="min-w-64 flex-1">
                <CampoDeBusca
                  valor={busca}
                  aoMudar={setBusca}
                  rotulo="Buscar disciplina"
                  placeholder="Nome da disciplina ou do professor"
                  aoLimpar={busca ? () => setBusca("") : undefined}
                />
              </div>

              <label className="flex min-h-12 items-center gap-2 text-sm text-cinza">
                <input
                  type="checkbox"
                  checked={mostrarInativas}
                  onChange={(e) => setMostrarInativas(e.target.checked)}
                  className="size-4 accent-acento"
                />
                Mostrar desativadas
                {inativas > 0 && <span className="text-xs text-cinza">({inativas})</span>}
              </label>

              <button
                type="button"
                data-nova-disciplina
                onClick={() => {
                  setRecado(null);
                  setForm({
                    id: null,
                    curso,
                    nome: "",
                    cargaHoraria: "",
                    docenteId: "",
                  });
                }}
                className="pill-cta min-h-12 px-5"
              >
                + Nova disciplina
              </button>
            </>
          )}
        </div>

        {/* A linha de resumo só existe COM curso: sem ele, o convite já está
            na caixa abaixo, e dizê-lo duas vezes na mesma dobra é a repetição
            que a régua da casa manda contar antes de dar a tela por pronta. */}
        {lista !== null && (
        <p className="mt-4 text-sm text-cinza">
          {lista.length} disciplina(s) neste curso
          {/* Zero grande é ruído — os avisos só aparecem quando há o que avisar. */}
          {incompletas > 0 && ` · ${incompletas} com menos de 10 questões`}
          {semDocente > 0 && ` · ${semDocente} sem professor definido`}
        </p>
        )}
      </div>

      {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}

      {/* ── O formulário ───────────────────────────────────────────────────── */}
      {form && (
        <section
          ref={caixaDoForm}
          className="surface rise p-6 sm:p-7"
          role="group"
          aria-labelledby="titulo-form-disciplina"
        >
          <h3 id="titulo-form-disciplina" className="font-display text-lg font-bold text-titulo">
            {form.id ? "Editar disciplina" : "Nova disciplina"}
          </h3>

          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5 sm:col-span-2">
              <span className="text-sm font-semibold text-tinta">Nome</span>
              <input
                data-campo="nome"
                className={CAMPO}
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                placeholder="Como aparece no boletim e no histórico do aluno"
              />
            </label>

            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold text-tinta">Curso</span>
              <select
                data-campo="curso"
                className={CAMPO}
                value={form.curso}
                disabled={form.id !== null}
                onChange={(e) => setForm({ ...form, curso: e.target.value })}
              >
                {cursos.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.nome ?? c.slug}
                  </option>
                ))}
              </select>
              {/* O campo desabilitado sem explicação é o operador procurando o
                  que ele fez de errado. A razão vai junto. */}
              {form.id !== null && (
                <span className="text-xs text-cinza">
                  A disciplina não troca de curso: ela está nas grades das turmas
                  deste. Para mudar, crie no curso certo e desative esta.
                </span>
              )}
            </label>

            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold text-tinta">Carga horária</span>
              <input
                data-campo="carga"
                className={CAMPO}
                value={form.cargaHoraria}
                onChange={(e) =>
                  setForm({ ...form, cargaHoraria: e.target.value.replace(/\D/g, "").slice(0, 3) })
                }
                placeholder="em horas"
                inputMode="numeric"
              />
              <span className="text-xs text-cinza">
                Entra no certificado: ele soma as horas da grade da turma.
              </span>
            </label>

            <label className="flex flex-col gap-1.5 sm:col-span-2">
              <span className="text-sm font-semibold text-tinta">Professor</span>
              <select
                data-campo="docente"
                className={CAMPO}
                value={form.docenteId}
                onChange={(e) => setForm({ ...form, docenteId: e.target.value })}
              >
                <option value="">Sem professor definido</option>
                {docentes.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.nome} — {d.nivel_texto}
                  </option>
                ))}
              </select>
              <span className="text-xs text-cinza">
                Só os docentes ativos aparecem aqui. O nome e o nível vão impressos
                no certificado do aluno.
              </span>
            </label>
          </div>

          <div className="mt-5 flex flex-wrap gap-3">
            <button
              type="button"
              data-salvar-disciplina
              onClick={() => void salvar()}
              disabled={salvando || !form.nome.trim() || !form.curso}
              className="pill-cta min-h-12 px-6 disabled:opacity-50"
            >
              {salvando ? "Salvando…" : form.id ? "Salvar" : "Cadastrar"}
            </button>
            <button
              type="button"
              onClick={() => setForm(null)}
              className="min-h-12 rounded-full border border-borda px-6 text-sm font-semibold text-cinza transition hover:bg-chip hover:text-tinta"
            >
              Cancelar
            </button>
          </div>
        </section>
      )}

      {/* ── A lista ────────────────────────────────────────────────────────── */}
      <section className="surface rise overflow-hidden">
        {lista === null ? (
          /* O convite, e não "nenhuma disciplina": a ausência de ESCOLHA e a
             ausência de RESULTADO são estados diferentes, e dizer o segundo
             quando é o primeiro faria o operador achar que o curso está vazio.
             É a família do "Limite com a prorrogação" do HUB DP — o que a tela
             não diz, o olho preenche com a suposição errada. */
          <p data-escolha-curso className="p-6 text-sm text-cinza">
            Escolha um curso acima para ver, criar e corrigir as disciplinas dele.
          </p>
        ) : lista.length === 0 ? (
          <p className="p-6 text-sm text-cinza">
            {busca.trim()
              ? "Nenhuma disciplina neste curso para esta busca."
              : "Este curso ainda não tem disciplina cadastrada."}
          </p>
        ) : (
          <ul className="divide-y divide-borda">
            {lista.slice(0, mostrando).map((d) => (
              <li
                key={d.id}
                data-disciplina={d.id}
                className={`flex flex-wrap items-start gap-4 p-5 ${d.ativa ? "" : "bg-chip/40"}`}
              >
                <div className="min-w-56 flex-1">
                  <p className="font-semibold text-titulo">
                    {d.nome}
                    {!d.ativa && (
                      <span className="ml-2 rounded-full bg-chip px-2 py-0.5 text-xs font-normal text-chip-tinta">
                        desativada
                      </span>
                    )}
                  </p>
                  <p className="mt-0.5 text-sm text-cinza">
                    {d.curso_slug}
                    {d.carga_horaria_horas ? ` · ${d.carga_horaria_horas}h` : ""}
                    {" · "}
                    {d.docente_nome
                      ? `${d.docente_nome}${d.docente_titulacao ? ` (${d.docente_titulacao})` : ""}`
                      : "sem professor"}
                  </p>
                  <p className="mt-1 flex flex-wrap gap-x-4 text-sm text-cinza">
                    {/* Abaixo de 10 a prova NÃO funciona — e é este o número que
                        o operador precisa ver sem abrir a disciplina. O alerta é
                        laranja (`alerta`), nunca verde: a família `aviso` desta
                        casa é de CONFIRMAÇÃO. */}
                    {/* Só quando é PROBLEMA. A prova completa é o caso comum
                        (213 das 245), e dizê-lo em cada linha é ruído — quem
                        precisa do total lê no topo. */}
                    {d.questoes_ativas < 10 && (
                      <span
                        data-questoes={d.questoes_ativas}
                        className="rounded bg-alerta-fundo px-1.5 font-semibold text-alerta-tinta"
                      >
                        {d.questoes_ativas} de 10 questões
                      </span>
                    )}
                    <span>{d.tentativas} tentativa(s)</span>
                    <span>
                      {d.turmas === 0 ? "fora de qualquer grade" : `em ${d.turmas} turma(s)`}
                    </span>
                  </p>
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    data-editar={d.id}
                    onClick={() => {
                      setRecado(null);
                      setForm({
                        id: d.id,
                        curso: d.curso_slug,
                        nome: d.nome,
                        cargaHoraria: d.carga_horaria_horas ? String(d.carga_horaria_horas) : "",
                        docenteId: d.docente_id ?? "",
                      });
                    }}
                    className="min-h-10 rounded-full border border-borda px-4 text-sm font-semibold text-cinza transition hover:bg-chip hover:text-tinta"
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    data-alternar={d.id}
                    onClick={() => void alternarAtiva(d)}
                    className="min-h-10 rounded-full border border-borda px-4 text-sm font-semibold text-cinza transition hover:bg-chip hover:text-tinta"
                  >
                    {d.ativa ? "Desativar" : "Reativar"}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {lista !== null && lista.length > mostrando && (
          <div className="border-t border-borda p-4 text-center">
            <button
              type="button"
              data-mostrar-mais
              onClick={() => setMostrando((n) => n + 25)}
              className="min-h-10 rounded-full border border-borda px-5 text-sm font-semibold text-cinza transition hover:bg-chip hover:text-tinta"
            >
              Mostrar mais ({lista.length - mostrando} restantes)
            </button>
          </div>
        )}
      </section>

      <p className="text-sm text-cinza">
        Disciplina não se apaga: as notas dos alunos apontam para ela. Desativar
        tira desta lista e impede que ela entre em grade nova — a turma que já a
        cursa continua com ela, e quem tira de lá é o editor de grade, na aba
        Turmas.
      </p>
    </div>
  );
}
